'use client'
import { logger, walletConnectorEvents } from '@dynamic-labs/wallet-connector-core';
import { getApiProviders } from '../getApiProviders/getApiProviders.js';
import { getEnabledProviders } from '../getEnabledProviders/getEnabledProviders.js';
import { filterWalletsForPlatform } from './filterWalletsForPlatform/filterWalletsForPlatform.js';
import { applyLinksOverrides } from './applyLinksOverrides/applyLinksOverrides.js';
import { defaultWalletUiUtils } from './const.js';

const getSupportedWallets = (args) => {
    var _a;
    const { appLogoUrl = '', appName = '', coinbaseWalletPreference, chainRpcProviders, deepLinkPreference, flowNetwork, mobileExperience = 'in-app-browser', networkConfigurations = { cosmos: [], evm: [], solana: [], starknet: [] }, settings, skipMemo = false, walletConnectProjectId = '', walletConnectorsProp, walletUiUtils = undefined, walletBook, walletConnectPreferredChains, } = args;
    let walletConnectors = [];
    if (!skipMemo && walletConnectors.length > 0) {
        return walletConnectors;
    }
    const evmNetworkConfigs = (networkConfigurations === null || networkConfigurations === void 0 ? void 0 : networkConfigurations.evm) || [];
    const solanaNetworkConfigs = (networkConfigurations === null || networkConfigurations === void 0 ? void 0 : networkConfigurations.solana) || [];
    const cosmosNetworkConfigs = (networkConfigurations === null || networkConfigurations === void 0 ? void 0 : networkConfigurations.cosmos) || [];
    const starknetNetworkConfigs = (networkConfigurations === null || networkConfigurations === void 0 ? void 0 : networkConfigurations.starknet) || [];
    const apiProviders = getApiProviders(getEnabledProviders(settings.providers));
    const disabledConnectors = (_a = settings.sdk.disabledWalletConnectors) !== null && _a !== void 0 ? _a : [];
    const opts = {
        apiProviders,
        appLogoUrl,
        appName,
        chainRpcProviders,
        coinbaseWalletPreference,
        cosmosNetworks: cosmosNetworkConfigs,
        deepLinkPreference,
        evmNetworks: evmNetworkConfigs,
        flowNetwork,
        mobileExperience,
        projectId: walletConnectProjectId,
        settings,
        solNetworks: solanaNetworkConfigs,
        starknetNetworks: starknetNetworkConfigs,
        walletBook,
        walletConnectPreferredChains,
        walletConnectorEventsEmitter: walletConnectorEvents,
        walletUiUtils: walletUiUtils || defaultWalletUiUtils,
    };
    const allWalletConnectors = walletConnectorsProp
        .map((getWalletConnectorConstructors) => getWalletConnectorConstructors(opts))
        .flat()
        .map((walletConnectorConstructor) => {
        try {
            // This may contain third party code so we must be ready for anything
            return new walletConnectorConstructor(opts);
        }
        catch (error) {
            logger.error(`Failed to construct wallet ${walletConnectorConstructor.name}`, error);
            return undefined;
        }
    })
        // filter out undefined and disabled connectors
        .filter((walletConnector) => {
        var _a;
        return walletConnector &&
            (!((_a = walletConnector.metadata) === null || _a === void 0 ? void 0 : _a.id) ||
                !disabledConnectors.includes(walletConnector.metadata.id));
    })
        // initialize the wallet connector if it's not a WalletConnect connector
        // or just the generic 'walletconnect' connector, not other WC connectors
        .map((walletConnector) => {
        if (!(walletConnector === null || walletConnector === void 0 ? void 0 : walletConnector.isWalletConnect) ||
            walletConnector.key === 'walletconnect') {
            walletConnector === null || walletConnector === void 0 ? void 0 : walletConnector.init();
        }
        return walletConnector;
    })
        // applies custom filter defined in the wallet connector
        .filter((walletConnector) => walletConnector === null || walletConnector === void 0 ? void 0 : walletConnector.filter())
        .map((walletConnector) => walletConnector.getMobileOrInstalledWallet());
    // Filter out duplicated wallets (WC vs. non-WC)
    const nonDuplicatedWalletConnectors = allWalletConnectors.filter((walletConnector) => {
        if (!walletConnector.isWalletConnect)
            return true;
        // if current wallet is WalletConnect, check if there is another wallet with the same key that is
        // not WalletConnect
        // if there is, return false(filter out WC wallet)
        // if there is no other wallet with the same key that is not WalletConnect, return true (keep WC wallet)
        return !allWalletConnectors.some((wc) => wc.key === walletConnector.key &&
            !wc.isWalletConnect &&
            wc.isInstalledOnBrowser());
    });
    const filteredWalletConnectors = filterWalletsForPlatform(walletBook, nonDuplicatedWalletConnectors);
    walletConnectors = applyLinksOverrides(walletBook, filteredWalletConnectors);
    return walletConnectors;
};

export { getSupportedWallets };
